import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { api, credentialRefused, setCredentials, unwrap, type Credentials } from '../api/client'
import type { AddressNode, Me, OwnerNode, ProjectNode } from '../api/types'
import { forgetHeldActions } from '../documents/held'
import { addCatalogue, setLanguage } from '../i18n'

/**
 * Who is signed in, and which project they are looking at.
 *
 * The credential lives in `localStorage` and the project lives in the URL.
 * That split is deliberate: a link somebody pastes into the project chat has
 * to open the same project for the person who clicks it, and it must not carry
 * their identity.
 */

const STORAGE_KEY = 'sitefocus.cm.credentials'

function readStored(): Credentials {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const mode = (parsed as { mode?: unknown }).mode
    if (mode === 'trusted-header') {
      const personId = (parsed as { personId?: unknown }).personId
      return typeof personId === 'string' ? { mode, personId } : null
    }
    if (mode === 'jwt' || mode === 'device' || mode === 'password' || mode === 'federated') {
      const token = (parsed as { token?: unknown }).token
      return typeof token === 'string' ? { mode, token } : null
    }
    return null
  } catch {
    // A locked-down browser can refuse storage outright. Signing in again each
    // time is worse than a crash on the first paint, but only just.
    return null
  }
}

function writeStored(next: Credentials): void {
  try {
    if (next === null) window.localStorage.removeItem(STORAGE_KEY)
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* nothing to do: the session simply will not survive a reload */
  }
}

export type SessionValue = {
  credentials: Credentials
  signIn: (credentials: Credentials) => void
  signOut: () => void
  me: UseQueryResult<Me>
  /**
   * The instance refused the credential this browser was holding.
   *
   * Separate from the credential itself, because the two have to outlive each
   * other in opposite directions: the credential is dropped the moment it is
   * refused, and the sentence explaining why is what the person needs to still
   * be on the screen afterwards. Cleared by signing in and by signing out.
   */
  refused: boolean
  /**
   * Whether the instance's own wording is missing, and the way to ask again.
   *
   * A boolean and a function rather than the query itself, because the shell
   * has one thing to decide with it and no business refetching anything else.
   */
  wording: { failed: boolean; retry: () => void }
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const queries = useQueryClient()
  const [credentials, setLocal] = useState<Credentials>(() => {
    const stored = readStored()
    setCredentials(stored)
    return stored
  })
  const [refused, setRefused] = useState(false)

  const signIn = useCallback((next: Credentials) => {
    setRefused(false)
    setCredentials(next)
    writeStored(next)
    setLocal(next)
    // A session begins here, and the one before it ends. Unresolved marks,
    // changes and pins are held in the tab rather than on the screen that made
    // them, so a browser that was signed out below and is signed in again is
    // the same tab with the same store; whoever is arriving must not be
    // offered the last person's work, and an answer still on its way to the
    // last person must not land in theirs. Called on both doors rather than
    // only on the way out, because a session can also end without anybody
    // pressing anything — see the refusal below.
    forgetHeldActions()
  }, [])

  const signOut = useCallback(() => {
    // A session this box issued is a row on it, so ending one has to be said
    // out loud — and the two kinds are ended at their own routes, because a
    // tablet and a browser are told apart everywhere else and this is not the
    // place to start blurring them. The other two modes have nothing to tell
    // anybody: a directory token is stateless and a trusted header was never a
    // session at all. This is sent before the credential is dropped, because
    // the credential is what authorises it, and it is not waited on — a tablet
    // whose uplink is down still signs out of the screen in front of the person
    // holding it, and the row on the box expires at the end of the shift
    // regardless.
    if (credentials?.mode === 'device') {
      void api.POST('/signin/device/sign-out', {}).catch(() => {
        /* the shift expiry is the backstop; nothing here can retry usefully */
      })
    }
    // Both browser doors end at the same route, because they opened the same
    // row: the federated door mints an ordinary browser session and the name
    // on the route is the door that needed it first. Signing out here ends
    // this application's session and nothing at the provider — somebody who
    // wants to leave the provider too signs out there, and a button here that
    // claimed to do it would be claiming authority over a tenant's identity
    // that this application does not have.
    if (credentials?.mode === 'password' || credentials?.mode === 'federated') {
      void api.POST('/signin/password/sign-out', {}).catch(() => {
        /* as above: the session expires on its own if this never arrives */
      })
    }
    setRefused(false)
    setCredentials(null)
    writeStored(null)
    setLocal(null)
    // Unresolved marks, changes and pins are held in the tab rather than on
    // the screen that made them, so that leaving a drawing does not throw
    // away a write that may be on it. That hold is one person's unfinished
    // work: the next person to sign in at this browser is not them, and must
    // not be offered a mark to send under somebody else's name.
    forgetHeldActions()
  }, [credentials])

  const me = useQuery({
    queryKey: ['session', 'me', credentials],
    enabled: credentials !== null,
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const answer = unwrap(await api.GET('/session/me', {}))
      // The reader's own setting, not the browser's locale and not the
      // project's: two people share one screen in a trailer.
      setLanguage(answer.person.language)
      return answer
    },
  })

  // The instance's own strings, taken once per language.
  //
  // The API never sends a sentence: a court entry is `court.action.respond`, a
  // refusal is `deficiency.error.photo_required_to_issue`, an event is a
  // summary key. Those keys belong to the modules loaded on this box, so the
  // client asks the box for them rather than keeping a copy that goes stale
  // the first time a module rewords one — or, worse, renders the raw key at
  // somebody in a trailer.
  //
  // **Held in a variable, and that is the repair.** Discarding the result threw
  // away the only thing that knows this failed, so a catalogue that never
  // arrived rendered `rfi.action.respond` at somebody in a trailer with nothing
  // on the screen to say why — the exact outcome the paragraph above says this
  // query exists to prevent. The shell now reads `wording.failed` and says so.
  //
  // `retry: false` was the other half. This deployment restarts its containers
  // on every run, so a sign-in during that window is a proxy answering once
  // while the application comes back; one attempt turned a few seconds of
  // restart into a whole session written in keys. Two retries with the default
  // backoff ride that window out.
  //
  // The finite `staleTime` is the smaller of the three and is worth stating
  // narrowly rather than being claimed as the fix: a query that errored holds
  // no data and is stale whatever this says, so what a finite time changes is
  // the *successful* case — a catalogue read hours ago is re-read rather than
  // held for the life of the tab, which is what a module rewording a state
  // needs. Half an hour is far longer than a restart and far shorter than a
  // shift.
  const language = me.data?.person.language
  const messages = useQuery({
    queryKey: ['session', 'messages', language],
    enabled: language !== undefined,
    staleTime: 30 * 60 * 1000,
    retry: 2,
    queryFn: async () => {
      const answer = unwrap(
        await api.GET('/session/messages', { params: { query: { lang: language } } }),
      )
      addCatalogue(answer.language, answer.messages)
      // A key with no translation in the language asked for comes back in
      // English, and the contract says a fallback is shown rather than hidden.
      // Nothing renders that yet; the count is here so it is not invisible.
      if (answer.fell_back.length > 0) {
        console.info(`${answer.fell_back.length} message(s) fell back to English`)
      }
      return answer
    },
  })

  // A credential the instance has refused is stopped being held.
  //
  // Dropping it is the half that was missing, and leaving it in place is what
  // made the two guards disagree. React Query keeps the last successful answer
  // beside a failed refetch, so after one 401 this browser held `me.data` and
  // a 401 at the same time: the gate read the refusal and sent the person to
  // the door, and the door read the data and sent them back through the gate.
  // Neither was wrong about what it looked at; there was simply no single
  // place where the session was actually declared over.
  //
  // **Only a refusal, still.** This is the same predicate #235 turned on: a
  // 500, a 502 from the proxy while the container comes back, or a `fetch`
  // that rejects carries no verdict about the credential, so none of them
  // reaches here and none of them signs anybody out. The cached answer is
  // dropped with the credential, because an answer obtained with a credential
  // the instance no longer accepts is not a thing to go on rendering.
  useEffect(() => {
    if (credentials === null) return
    if (!credentialRefused(me.error)) return
    setRefused(true)
    setCredentials(null)
    writeStored(null)
    setLocal(null)
    queries.removeQueries({ queryKey: ['session', 'me'] })
    // The same session ending, without anybody pressing anything — which is
    // the half that was missing. Signing out let go of the unresolved marks,
    // changes and pins held in this tab; a credential the instance refuses
    // ends the session just as finally, and left them there for whoever signs
    // in next. An answer still on its way to the refused session is let go of
    // with them: the era moves, so it is already out of date when it arrives.
    forgetHeldActions()
  }, [credentials, me.error, queries])

  const value = useMemo<SessionValue>(
    () => ({
      credentials,
      signIn,
      signOut,
      me,
      refused,
      wording: { failed: messages.isError, retry: () => void messages.refetch() },
    }),
    [credentials, signIn, signOut, me, refused, messages],
  )
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (value === null) throw new Error('useSession outside a SessionProvider')
  return value
}

/**
 * The signed-in person's own id, or null when it is not known here.
 *
 * For a screen that offers somebody their own record and nobody else's, which
 * a tablet PIN is. Null rather than a throw outside a provider, so a screen
 * drawn on its own offers the narrower thing, never the wider one.
 */
export function useOwnPersonId(): string | null {
  return useContext(SessionContext)?.me.data?.person.id ?? null
}

/**
 * Who is signed in, by the account's opaque id, for a screen that keys a cache by it.
 *
 * Nothing about the person but the id: never the address or the name, which
 * are the person's to change and are not what tells two accounts apart.
 * `null` under a provider with nobody identified -- signed out, refused, or not
 * yet read -- which a screen reads as "ask nothing". `undefined` where there is
 * no provider at all, which is a screen rendered on its own, and which
 * {@link useSession} would refuse outright. Read-only: it changes nothing the
 * session does.
 */
export function useSessionPersonId(): string | null | undefined {
  const value = useContext(SessionContext)
  if (value === null) return undefined
  return value.me.data?.person.id ?? null
}

export type ProjectPlacement = {
  project: ProjectNode
  address: AddressNode
  owner: OwnerNode
}

/** Every project this person may open, flattened with its owner and address. */
export function placements(me: Me | undefined): ProjectPlacement[] {
  if (!me) return []
  return me.owners.flatMap((owner) =>
    owner.addresses.flatMap((address) =>
      address.projects.map((project) => ({ project, address, owner })),
    ),
  )
}

export function placementFor(me: Me | undefined, projectId: string): ProjectPlacement | undefined {
  return placements(me).find((placement) => placement.project.id === projectId)
}

const RANK: Record<string, number> = { none: 0, read_only: 1, standard: 2, admin: 3 }

/**
 * The level this person holds on a tool, on the project they are looking at.
 *
 * Two maps rather than one, and the project is why. A level is granted on a
 * job: somebody administering one job and reading another holds both levels at
 * this address, and a single merged number would grey nothing on the job they
 * only read and offer them a button the server refuses. `tool_levels` is what a
 * company-scope grant gives them everywhere; `project_tool_levels` is what each
 * job gives them; this merges the two for the job on screen and no other, which
 * is exactly what the evaluator does on the instance.
 *
 * Used to grey a module out in the navigation, never to decide anything: the
 * evaluator on the instance decides, and this is a copy of the same numbers so
 * the menu agrees with the screen. A module the client has no level for is
 * still requested — a 403 from the API is the answer, not a hidden screen.
 */
export function levelFor(placement: ProjectPlacement | undefined, toolCode: string): string {
  if (!placement) return 'none'
  const everywhere = placement.owner.tool_levels[toolCode] ?? 'none'
  const here = placement.owner.project_tool_levels?.[placement.project.id]?.[toolCode] ?? 'none'
  return (RANK[here] ?? 0) > (RANK[everywhere] ?? 0) ? here : everywhere
}

export function canOpen(placement: ProjectPlacement | undefined, toolCode: string): boolean {
  return levelFor(placement, toolCode) !== 'none'
}

/**
 * Whether this person holds one granular permission on a tool, on this project.
 *
 * Scoped exactly the way {@link levelFor} scopes a level, and for the same
 * reason: a granular granted on one job is not held on another, and a client
 * that merged them would offer a button the server refuses on the second.
 *
 * **A level alone does not answer what somebody may do.** A granular permission
 * is how an administrator's action is given to somebody who is not one, so a
 * screen that reads only the level is asking a question whose answer it will
 * sometimes get wrong in the direction that matters — hiding a control from
 * somebody the instance would have let through. `/session/me` has carried both
 * maps all along; nothing here read them until a screen needed to.
 *
 * Like {@link levelFor} this decides what is *offered*, never what is allowed.
 * The evaluator on the instance decides, and these are a copy of its numbers so
 * the screen agrees with the refusal.
 */
export function holdsGranular(
  placement: ProjectPlacement | undefined,
  toolCode: string,
  code: string,
): boolean {
  if (!placement) return false
  const everywhere = placement.owner.tool_granulars?.[toolCode] ?? []
  const here = placement.owner.project_tool_granulars?.[placement.project.id]?.[toolCode] ?? []
  return everywhere.includes(code) || here.includes(code)
}
